import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiBriefcase, FiMapPin, FiSend, FiChevronRight, FiCheck } from 'react-icons/fi';

/**
 * SendOfferModal
 * Shows the company's job posts so the recruiter can pick one,
 * optionally add a personal note, then confirm sending the offer.
 *
 * Props:
 *   show        - boolean
 *   onClose     - () => void
 *   onConfirm   - (job, note) => Promise<void>
 *   jobs        - the company's posted jobs array
 *   candidate   - the student object being offered
 *   sending     - boolean (loading state)
 */
const SendOfferModal = ({ show, onClose, onConfirm, jobs = [], candidate, sending = false }) => {
    const [selectedJob, setSelectedJob] = useState(null);
    const [note, setNote] = useState('');
    const [step, setStep] = useState(1); // 1=select job, 2=confirm

    if (!show) return null;

    const handleClose = () => {
        setSelectedJob(null);
        setNote('');
        setStep(1);
        onClose();
    };

    const handleConfirm = async () => {
        if (!selectedJob) return;
        await onConfirm(selectedJob, note);
        setSelectedJob(null);
        setNote('');
        setStep(1);
    };

    const activeJobs = jobs.filter(j => j.status === 'approved' || !j.status || j.status === 'live');

    return (
        <div
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(10px)',
                zIndex: 30000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px',
            }}
            onClick={handleClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.93, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.93, y: 16 }}
                transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'var(--bg-main)',
                    border: '1px solid var(--border)',
                    borderRadius: '24px',
                    width: '100%',
                    maxWidth: '520px',
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.4)',
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    flexShrink: 0,
                }}>
                    <div>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '4px 10px', borderRadius: '8px',
                            background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)',
                            marginBottom: '8px',
                        }}>
                            <FiSend size={10} color="var(--accent)" />
                            <span style={{ fontSize: '10px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Send Offer
                            </span>
                        </div>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: 'var(--text-main)', letterSpacing: '-0.02em' }}>
                            Offer to {candidate?.fullName || candidate?.email || 'Student'}
                        </h2>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                            {step === 1 ? 'Select the role you want to offer' : 'Add a note and confirm'}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        style={{
                            width: '32px', height: '32px', borderRadius: '10px', flexShrink: 0,
                            background: 'var(--bg-surface)', border: '1px solid var(--border)',
                            color: 'var(--text-main)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <FiX size={16} />
                    </button>
                </div>

                {/* Progress bar */}
                <div style={{ height: '3px', background: 'var(--border)', flexShrink: 0 }}>
                    <motion.div animate={{ width: `${(step / 2) * 100}%` }} transition={{ duration: 0.3 }}
                        style={{ height: '100%', background: 'var(--accent)', borderRadius: '2px' }} />
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    <AnimatePresence mode="wait">

                        {/* STEP 1: Select Job */}
                        {step === 1 && (
                            <motion.div key="step1"
                                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                                style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                            >
                                {activeJobs.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                                        <FiBriefcase size={36} style={{ marginBottom: '12px', opacity: 0.3 }} />
                                        <p style={{ fontWeight: 700, color: 'var(--text-main)', marginBottom: '6px' }}>No live job posts</p>
                                        <p style={{ fontSize: '13px' }}>Post a job opportunity first, then you can send offers.</p>
                                    </div>
                                ) : (
                                    activeJobs.map(job => (
                                        <motion.button
                                            key={job.id}
                                            type="button"
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.99 }}
                                            onClick={() => setSelectedJob(job)}
                                            style={{
                                                width: '100%', textAlign: 'left', padding: '14px 16px',
                                                borderRadius: '14px', cursor: 'pointer',
                                                background: selectedJob?.id === job.id ? 'rgba(34,197,94,0.08)' : 'var(--bg-card)',
                                                border: `1.5px solid ${selectedJob?.id === job.id ? 'var(--accent)' : 'var(--border)'}`,
                                                transition: 'all 0.2s', outline: 'none',
                                                display: 'flex', alignItems: 'center', gap: '12px',
                                            }}
                                        >
                                            <div style={{
                                                width: '40px', height: '40px', borderRadius: '12px', flexShrink: 0,
                                                background: selectedJob?.id === job.id ? 'rgba(34,197,94,0.15)' : 'var(--bg-surface)',
                                                border: `1px solid ${selectedJob?.id === job.id ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: selectedJob?.id === job.id ? 'var(--accent)' : 'var(--text-muted)',
                                            }}>
                                                {selectedJob?.id === job.id ? <FiCheck size={18} /> : <FiBriefcase size={16} />}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: '14px', fontWeight: 800, color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {job.title}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: 'rgba(34,197,94,0.1)', color: 'var(--accent)', fontWeight: 700 }}>
                                                        {job.type}
                                                    </span>
                                                    {job.location && (
                                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                            <FiMapPin size={9} /> {job.location}
                                                        </span>
                                                    )}
                                                    {job.stipend && (
                                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{job.stipend}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {selectedJob?.id === job.id && (
                                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <FiCheck size={11} color="#fff" />
                                                </div>
                                            )}
                                        </motion.button>
                                    ))
                                )}
                            </motion.div>
                        )}

                        {/* STEP 2: Add Note & Confirm */}
                        {step === 2 && selectedJob && (
                            <motion.div key="step2"
                                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                            >
                                {/* Selected job summary */}
                                <div style={{
                                    padding: '14px 16px', borderRadius: '14px',
                                    background: 'rgba(34,197,94,0.06)', border: '1.5px solid rgba(34,197,94,0.2)',
                                }}>
                                    <div style={{ fontSize: '10px', fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Selected Role</div>
                                    <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-main)' }}>{selectedJob.title}</div>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: 'rgba(34,197,94,0.1)', color: 'var(--accent)', fontWeight: 700 }}>{selectedJob.type}</span>
                                        {selectedJob.location && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{selectedJob.location}</span>}
                                        {selectedJob.stipend && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{selectedJob.stipend}</span>}
                                    </div>
                                </div>

                                {/* Personal note */}
                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '8px' }}>
                                        Personal Note <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span>
                                    </label>
                                    <textarea
                                        value={note}
                                        onChange={e => setNote(e.target.value)}
                                        placeholder={`Hi ${candidate?.fullName?.split(' ')[0] || 'there'}, we'd love to have you on board for the ${selectedJob.title} role...`}
                                        rows={4}
                                        style={{
                                            width: '100%', boxSizing: 'border-box',
                                            padding: '12px 14px', borderRadius: '12px',
                                            background: 'var(--bg-card)', border: '1.5px solid var(--border)',
                                            color: 'var(--text-main)', fontSize: '14px', fontFamily: 'inherit',
                                            resize: 'vertical', outline: 'none',
                                        }}
                                        onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                                        onBlur={e => e.target.style.borderColor = 'var(--border)'}
                                    />
                                </div>

                                {/* Candidate summary */}
                                <div style={{ padding: '12px 14px', borderRadius: '12px', background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '16px', color: 'var(--accent)', overflow: 'hidden', flexShrink: 0 }}>
                                        {candidate?.photo ? <img src={candidate.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (candidate?.fullName?.[0] || '?').toUpperCase()}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-main)' }}>{candidate?.fullName || 'Student'}</div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{candidate?.email}</div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '16px 24px', borderTop: '1px solid var(--border)', flexShrink: 0,
                    display: 'flex', justifyContent: 'space-between', gap: '10px',
                }}>
                    {step === 2 ? (
                        <motion.button
                            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                            type="button" onClick={() => setStep(1)}
                            style={{
                                height: '42px', padding: '0 18px', borderRadius: '11px',
                                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                                color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
                            }}
                        >
                            ← Back
                        </motion.button>
                    ) : (
                        <button type="button" onClick={handleClose}
                            style={{ height: '42px', padding: '0 18px', borderRadius: '11px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>
                            Cancel
                        </button>
                    )}

                    {step === 1 ? (
                        <motion.button
                            whileHover={selectedJob ? { scale: 1.02 } : {}}
                            whileTap={selectedJob ? { scale: 0.97 } : {}}
                            type="button"
                            disabled={!selectedJob}
                            onClick={() => setStep(2)}
                            style={{
                                height: '42px', padding: '0 22px', borderRadius: '11px',
                                background: selectedJob ? 'var(--accent)' : 'var(--bg-surface)',
                                border: 'none', color: selectedJob ? '#fff' : 'var(--text-muted)',
                                cursor: selectedJob ? 'pointer' : 'not-allowed',
                                fontSize: '14px', fontWeight: 800,
                                display: 'flex', alignItems: 'center', gap: '6px',
                                boxShadow: selectedJob ? '0 4px 12px rgba(34,197,94,0.25)' : 'none',
                                transition: 'all 0.2s',
                            }}
                        >
                            Continue <FiChevronRight size={16} />
                        </motion.button>
                    ) : (
                        <motion.button
                            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                            type="button"
                            disabled={sending}
                            onClick={handleConfirm}
                            style={{
                                height: '42px', padding: '0 24px', borderRadius: '11px',
                                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                border: 'none', color: '#fff',
                                cursor: sending ? 'not-allowed' : 'pointer',
                                fontSize: '14px', fontWeight: 800,
                                display: 'flex', alignItems: 'center', gap: '8px',
                                boxShadow: '0 4px 16px rgba(34,197,94,0.3)',
                                opacity: sending ? 0.7 : 1,
                            }}
                        >
                            <FiSend size={15} /> {sending ? 'Sending...' : 'Send Offer'}
                        </motion.button>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default SendOfferModal;
